import { useFetcher } from '@remix-run/react';
import { useEffect, useId, useRef } from 'react';

export type StableFetcherData<TData> = ReturnType<
  typeof useFetcher<TData>
>['data'];

export type UseStableFetcherOptions<TData> = {
  initialData?: StableFetcherData<TData> | null;
  key?: string;
};

/**
 * A custom hook that provides a stable fetcher.
 * @returns A fetcher that retains the last fetched data even if the fetcher data is empty.
 */
export function useStableFetcher<TData = unknown>({
  initialData,
  key,
}: UseStableFetcherOptions<TData> = {}) {
  const instanceId = useId();
  const keyGenerationRef = useRef(0);
  const previousKeyRef = useRef(key);
  const previousDataRef = useRef<StableFetcherData<TData> | null | undefined>(
    initialData ?? undefined
  );

  if (previousKeyRef.current !== key) {
    keyGenerationRef.current += 1;
    previousKeyRef.current = key;
    previousDataRef.current = initialData ?? undefined;
  }

  const fetcher = useFetcher<TData>({
    key: `${instanceId}:${keyGenerationRef.current}:${key ?? 'idle'}`,
  });

  useEffect(() => {
    if (initialData === undefined) return;
    // Only seed previousDataRef from initialData if we don't already have data.
    if (
      previousDataRef.current !== undefined &&
      previousDataRef.current !== null
    ) {
      return;
    }

    previousDataRef.current = initialData;
  }, [initialData]);

  useEffect(() => {
    if (fetcher.data === undefined || fetcher.data === null) return;

    previousDataRef.current = fetcher.data;
  }, [fetcher.data]);

  return {
    ...fetcher,
    stableData: (fetcher.data ?? initialData ?? previousDataRef.current) as
      | StableFetcherData<TData>
      | null
      | undefined,
  };
}
